import * as React from 'react';
import { useState } from 'react';
import Row from 'components/Row';
import Text from 'components/Text';
import Link from 'components/Link';
import Field from 'components/Field';
import Radio from 'components/Radio';
import Image from 'components/Image';
import Button from 'components/Button';
import BoxBody from 'components/BoxBody';
import BoxHeader from 'components/BoxHeader';
import ClaimDeviceClasses from 'user/components/ClaimDeviceClasses';
import { ConfirmableDeviceType, deviceString } from 'entities/Device';

import './styles/ClaimDevice.scss';
import { isFeatureEnabled } from 'lib/FeatureFlag';
import Divider from 'components/Divider';
import LinkConstants from 'constants/LinkConstants';

export type Props = {
  deviceClasses: string[];
  claimToken: string;
  deviceAddress: string;
  deviceType: ConfirmableDeviceType;
  mergeAccounts: boolean;
  submitDisabled?: boolean;
  displayMergeAccountsConfirmation: boolean;
  onMergeAccountsChange: (mergeAccounts: boolean) => void;
  onClaim: () => void;
  onConfirmMerge: () => void;
  onCancelMerge: () => void;
};

export default function ClaimDevice(props: Props) {
  const ACCOUNT_NOT_RECOGNIZED_OPTION = 'account_not_recognized';
  const ACCOUNT_BELONGS_TO_FAMILY_MEMBER_OPTION = 'account_belongs_to_family_member';
  const ACCOUNT_BELONGS_TO_USER_OPTION = 'account_belongs_to_user';

  const betterUXForDeviceStealingAndAccountMergingEnabled = isFeatureEnabled(
    'better-ux-device-stealing-user-merging.web.sep-23'
  );

  const deviceName = deviceString(props.deviceAddress, props.deviceType);
  const [option, selectOption] = useState(ACCOUNT_NOT_RECOGNIZED_OPTION);

  const renderMergeAccountsConfirmation = () => {
    return (
      <div
        data-testid={'claim-device-merge-accounts-confirmation'}
        className="MergeAccountsConfirmation"
      >
        <BoxHeader title="Merge accounts?" size="small" />
        <BoxBody>
          <Row>
            <Text color="graphite">
              When you merge accounts, you'll have access to all your classes in one account. You'll
              also lose the conversation history for the duplicate account. Consider obtaining your{' '}
              <Link weight={'semibold'} href={LinkConstants.MESSAGE_HISTORY_HELP} target="_blank">
                message history
              </Link>{' '}
              for the duplicate account first.
            </Text>
          </Row>
          <Row size="small">
            <Button
              color="apple"
              onClick={props.onConfirmMerge}
              disabled={props.submitDisabled}
              loading={props.submitDisabled}
              data-testid="confirm-merge-accounts-button"
            >
              Merge accounts
            </Button>
          </Row>
          <Link color="apple" onClick={props.onCancelMerge}>
            Cancel
          </Link>
        </BoxBody>
      </div>
    );
  };

  function isAccountNotRecognizedSelected() {
    return option === ACCOUNT_NOT_RECOGNIZED_OPTION;
  }

  function isAccountBelongsToFamilyMemberSelected() {
    return option === ACCOUNT_BELONGS_TO_FAMILY_MEMBER_OPTION;
  }

  function isAccountBelongsToUserSelected() {
    return option === ACCOUNT_BELONGS_TO_USER_OPTION;
  }

  function renderOption(
    label: string,
    labelNote: string,
    isSelected: boolean,
    onOptionSelected: () => void,
    withWarning: boolean,
    dataTestId: string,
    boldLabelNote?: string
  ) {
    return (
      <Radio
        data-testid={dataTestId}
        size="small"
        label={label}
        customLabelNote={
          <Text
            color={isSelected ? 'inherit' : 'graphite'}
            element="span"
            weight="normal"
            className="Label-note"
          >
            <div
              className={withWarning ? 'ClaimDevice-mergeWarning' : undefined}
              data-testid={withWarning ? dataTestId + '-warning' : dataTestId + '-label'}
            >
              <Text
                color={isSelected ? 'inherit' : 'graphite'}
                element="span"
                weight="bold"
                className="Label-note"
              >
                {boldLabelNote && boldLabelNote + ' '}
              </Text>
              {labelNote}
            </div>
          </Text>
        }
        checked={isSelected}
        onChange={onOptionSelected}
      />
    );
  }

  function renderAccountBelongsToFamilyMemberOption() {
    return (
      <Row size="small">
        {renderOption(
          'That account belongs to a member of my family',
          'will be added to your account and removed from their account',
          isAccountBelongsToFamilyMemberSelected(),
          () => {
            selectOption(ACCOUNT_BELONGS_TO_FAMILY_MEMBER_OPTION);
            props.onMergeAccountsChange(false);
          },
          false,
          'account-belongs-to-family-member-option',
          deviceName
        )}
      </Row>
    );
  }

  function renderThatIsAlsoMyAccountOption() {
    let optionComponent = renderOption(
      "That's also my account",
      'Your accounts will be merged',
      isAccountBelongsToUserSelected(),
      () => {
        selectOption(ACCOUNT_BELONGS_TO_USER_OPTION);
        props.onMergeAccountsChange(true);
      },
      false,
      'merge-accounts-option'
    );

    if (betterUXForDeviceStealingAndAccountMergingEnabled) {
      optionComponent = renderOption(
        "That's also my account",
        "Be sure you're not merging your family member’s account into yours. If you do so, " +
          'your account may revert to a different role, and you may lose some app privileges',
        isAccountBelongsToUserSelected(),
        () => {
          selectOption(ACCOUNT_BELONGS_TO_USER_OPTION);
          props.onMergeAccountsChange(true);
        },
        true,
        'merge-accounts-option',
        'Your accounts will be permanently merged.'
      );
    }

    return <Row flush>{optionComponent}</Row>;
  }

  const renderClaimDevice = () => {
    const deviceTypeText = props.deviceType === 'email' ? 'email address' : 'phone number';

    return (
      <div className="ClaimDevice">
        <Image src={require('assets/images/claim-device.svg')} />
        <BoxHeader title="Existing account found" size="xsmall" />
        <div className="ClaimDevice-classes">
          <ClaimDeviceClasses
            deviceName={deviceName}
            deviceClasses={props.deviceClasses}
            displayLargeDevice={betterUXForDeviceStealingAndAccountMergingEnabled}
          />
        </div>
        <Divider size="flush"></Divider>
        <div className="ClaimDevice-options">
          <Field>
            <Row size="small" data-testid={'claim-device-account-not-recognized-option-label'}>
              {renderOption(
                `I don’t recognize that account, but that’s my ${deviceTypeText}`,
                'will be added to your account and removed from their account',
                isAccountNotRecognizedSelected(),
                () => {
                  selectOption(ACCOUNT_NOT_RECOGNIZED_OPTION);
                  props.onMergeAccountsChange(false);
                },
                false,
                'account-not-recognized-option',
                `Just ${deviceName}`
              )}
            </Row>
            {betterUXForDeviceStealingAndAccountMergingEnabled
              ? renderAccountBelongsToFamilyMemberOption()
              : null}
            {renderThatIsAlsoMyAccountOption()}
          </Field>
          <Button
            color="mascot"
            size="long"
            onClick={() => {
              props.onClaim();
            }}
            disabled={props.submitDisabled}
            loading={props.submitDisabled}
            data-testid={'submit-button'}
          >
            Submit
          </Button>
        </div>
      </div>
    );
  };

  return props.displayMergeAccountsConfirmation
    ? renderMergeAccountsConfirmation()
    : renderClaimDevice();
}
